import React, { useState } from 'react';
import { X, Package, Tag, IndianRupee, Hash, Plus } from 'lucide-react';
import { InputField } from './CommonComponents'

const ItemModal = ({ item, onSave, onClose }) => {
    const isRestock = item?.isRestock || false;
    const [formData, setFormData] = useState({
        itemName: item?.itemName || '',
        category: item?.category || '',
        purchasePrice: item?.purchasePrice || '',
        sellingPrice: item?.sellingPrice || '',
        quantity: isRestock ? '' : (item?.quantity || ''),
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        let updatedQuantity;
        if (isRestock) {
            // Restock: add new quantity to existing
            updatedQuantity = (parseInt(item.quantity, 10) || 0) + (parseInt(formData.quantity, 10) || 0);
        } else if (item?.id) {
            // Editing existing item: keep current quantity unchanged
            updatedQuantity = item.quantity;
        } else {
            // New item: use the form value
            updatedQuantity = parseInt(formData.quantity, 10) || 0;
        }

        const updatedItem = {
            ...item,
            ...formData,
            quantity: updatedQuantity,
        };

        onSave(updatedItem);
    };



    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className={`p-4 sm:p-6 border-b border-gray-200 ${isRestock ? 'bg-gradient-to-r from-green-50 to-green-100' :
                    item?.id ? 'bg-gradient-to-r from-blue-50 to-blue-100' :
                        'bg-gradient-to-r from-purple-50 to-purple-100'
                    }`}>
                    <div className="flex justify-between items-start">
                        <div className="flex items-center space-x-3 flex-1">
                            <div className={`p-2 sm:p-3 rounded-full ${isRestock ? 'bg-green-200' :
                                item?.id ? 'bg-blue-200' : 'bg-purple-200'
                                }`}>
                                {isRestock ? (
                                    <Plus className={`h-5 w-5 sm:h-6 sm:w-6 text-green-600`} />
                                ) : (
                                    <Package className={`h-5 w-5 sm:h-6 sm:w-6 ${item?.id ? 'text-blue-600' : 'text-purple-600'
                                        }`} />
                                )}
                            </div>
                            <div className="flex-1 min-w-0">
                                <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
                                    {isRestock ? 'Restock Item' : (item?.id ? 'Edit Item' : 'Add New Item')}
                                </h2>
                                <p className="text-sm text-gray-600 mt-1">
                                    {isRestock ? 'Add stock to existing item' :
                                        item?.id ? 'Update item information' :
                                            'Create a new inventory item'}
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-white hover:bg-opacity-50 rounded-full transition-colors ml-2"
                        >
                            <X size={20} className="sm:w-6 sm:h-6" />
                        </button>
                    </div>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-4 sm:p-6">
                    <div className="space-y-6">
                        {isRestock && (
                            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                <div className="flex items-center space-x-3">
                                    <Package className="h-5 w-5 text-green-600" />
                                    <div>
                                        <p className="font-semibold text-green-900">{item.itemName}</p>
                                        <p className="text-sm text-green-700">Current stock: {item.quantity} units</p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {!isRestock && (
                            <>
                                <InputField
                                    label="Item Name"
                                    name="itemName"
                                    value={formData.itemName}
                                    onChange={handleChange}
                                    icon={Package}
                                    placeholder="Enter item name"
                                    required
                                />
                                <InputField
                                    label="Category"
                                    name="category"
                                    value={formData.category}
                                    onChange={handleChange}
                                    icon={Tag}
                                    placeholder="Enter category"
                                />
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <InputField
                                        label="Purchase Price (₹)"
                                        name="purchasePrice"
                                        type="number"
                                        step="0.01"
                                        value={formData.purchasePrice}
                                        onChange={handleChange}
                                        icon={IndianRupee}
                                        placeholder="0.00"
                                        required
                                    />
                                    <InputField
                                        label="Selling Price (₹)"
                                        name="sellingPrice"
                                        type="number"
                                        step="0.01"
                                        value={formData.sellingPrice}
                                        onChange={handleChange}
                                        icon={IndianRupee}
                                        placeholder="0.00"
                                        required
                                    />
                                </div>

                                {/* Show quantity field only for new items, not when editing */}
                                {!item?.id && (
                                    <InputField
                                        label="Initial Quantity"
                                        name="quantity"
                                        type="number"
                                        value={formData.quantity}
                                        onChange={handleChange}
                                        icon={Hash}
                                        placeholder="0"
                                        required
                                    />
                                )}

                                {/* Show current stock as read-only info when editing */}
                                {item?.id && (
                                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                                        <div className="flex items-center space-x-3">
                                            <Hash className="h-5 w-5 text-gray-500" />
                                            <div>
                                                <p className="text-sm text-gray-500">Current Stock</p>
                                                <p className="font-semibold text-gray-900">{item.quantity} units</p>
                                            </div>
                                        </div>
                                        <p className="text-xs text-gray-400 mt-2">Use the Restock option to update quantity</p>
                                    </div>
                                )}
                            </>
                        )}

                        {isRestock && (
                            <InputField
                                label="Quantity to Add"
                                name="quantity"
                                type="number"
                                value={formData.quantity}
                                onChange={handleChange}
                                icon={Hash}
                                placeholder="0"
                                required
                            />
                        )}

                        {isRestock && formData.quantity && (
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                <p className="text-sm text-blue-700">
                                    <span className="font-semibold">New stock level:</span> {
                                        (parseInt(item.quantity, 10) || 0) + (parseInt(formData.quantity, 10) || 0)
                                    } units
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Actions */}
                    <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 sm:px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className={`px-4 sm:px-6 py-3 text-white rounded-lg font-medium transition-colors ${isRestock ? 'bg-green-600 hover:bg-green-700' :
                                item?.id ? 'bg-blue-600 hover:bg-blue-700' :
                                    'bg-purple-600 hover:bg-purple-700'
                                }`}
                        >
                            {isRestock ? 'Add Stock' : item?.id ? 'Update Item' : 'Create Item'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ItemModal;